import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Plus,
  FolderPlus,
  Clock,
  Settings,
  Globe,
  AlertCircle,
  CheckCircle2,
  Undo2,
  Redo2,
} from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  MeasuringStrategy,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { TodoItemResponse, TodoSectionResponse } from '@/lib/api';
import { useAuth } from '@/auth/hooks/useAuth';
import { useHolderSwitcher } from '@/components/HolderSwitcher';
import {
  useTodoList,
  useTodoSections,
  useTodoItems,
  useTodoListSubtasks,
  useTodoUndoRedo,
  useTodoShortcuts,
  usePrincipalDisplayNames,
  useTodoAssignmentNotifications,
  useTodoListReviewers,
  useTodoSubtaskIndex,
  useTodoSectionAccess,
  useTodoProjects,
  useTodoLists,
  useTodoListLinks,
  useTodoListMove,
} from '../hooks';
import { useListDetailHandlers } from '../hooks/useListDetailHandlers.js';
import {
  SectionCard,
  TodoItemRow,
  CreateSectionDialog,
  CreateTimeframeDialog,
  CreateItemDialog,
  EditSectionDialog,
  EditItemDialog,
  ListSettingsDialog,
  MoveToListDialog,
  AssigneeFilterBar,
  type AssigneeFilterValue,
  ReviewFilterBar,
  type ReviewFilterValue,
  TaskSearchBar,
  MyTasksBoard,
  ProjectGroupedView,
} from '../components';
import { TodoListProvider } from '../context';
import type { TodoListContextValue } from '../context';
import {
  itemContainerId,
  resolveItemDrop,
  reorderSelectedIds,
  parseItemContainerId,
  makeTodoCollisionDetection,
} from '../lib/dnd';
import { STATUS_STYLES, WORK_STATUSES, CANCEL_STATUS, COMPLETE_STATUS } from '../lib/status';
import { useMultiSelect } from '../hooks/useMultiSelect';
import { SelectionIndicator } from '../components/SelectionIndicator';
import type { BulkActionsProps } from '../components/BulkActionsMenuItems';
import { useToast } from '@/components/ui/use-toast';
import { parseSearchTerms, taskMatchesTerms } from '../lib/taskSearch';
import { buildSectionPath } from '../lib/sectionPath';
import { flattenSectionTree } from '../lib/sectionTree';

export function ListDetailPage() {
  const { listId } = useParams<{ listId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    selectedHolderId,
    selectedOption,
    principalId,
    isLoading: switcherLoading,
  } = useHolderSwitcher();

  const { list, loading, error } = useTodoList(selectedHolderId, listId!);
  const {
    sections,
    loading: sectionsLoading,
    createSection,
    updateSection,
    deleteSection,
  } = useTodoSections(selectedHolderId, listId!);
  const { accessBySection, setSectionAccess } = useTodoSectionAccess(selectedHolderId, listId!);
  const { projects, projectById, createProject, updateProject, deleteProject } =
    useTodoProjects(selectedHolderId);
  // Cross-list move: the workspace's other lists (move targets + link candidates),
  // this list's move shortcuts, and the move mutations.
  const { lists: workspaceLists } = useTodoLists(selectedHolderId);
  const { links, createLink, deleteLink } = useTodoListLinks(selectedHolderId, listId!);
  const { moveItem, moveSection } = useTodoListMove(selectedHolderId);
  const otherLists = useMemo(
    () =>
      workspaceLists
        .filter((l) => l.listId !== listId)
        .map((l) => ({ listId: l.listId, name: l.name })),
    [workspaceLists, listId],
  );
  const hasMoveTargets = otherLists.length > 0;
  const {
    items: rawItems,
    loading: itemsLoading,
    createItem,
    updateItem,
    deleteItem,
    reorderItems,
    assignItem,
    unassignItem,
  } = useTodoItems(selectedHolderId, listId!);
  // Sub-tasks for the whole list (grouped by parent), rendered nested + inert
  // under their parent row. Not part of the list's drag-and-drop.
  const { subtasksByParent } = useTodoListSubtasks(selectedHolderId, listId!);

  const { resolve: resolvePrincipalName, members: assignableMembers } = usePrincipalDisplayNames();
  const {
    loaded: notificationsLoaded,
    unreadItemIds,
    unreadSectionIds,
    unreadReviewItemIds,
    markAssignmentRead,
    markReviewRead,
    markListNotificationsRead,
  } = useTodoAssignmentNotifications();
  const isOrgList = list?.holderType === 'org';
  const { reviewerPrincipalIds, setReviewers } = useTodoListReviewers(
    selectedHolderId ?? '',
    listId!,
    isOrgList,
  );
  // Whether the current user is a designated reviewer of this list — grants the
  // Verify / Send-back affordances on in-review items they aren't assigned to.
  const isListReviewer = !!principalId && reviewerPrincipalIds.has(principalId);
  // Eligible "send for review" targets = the list's designated reviewers plus
  // owners/admins (who can always approve). Empty on Personal lists.
  const reviewerTargets = useMemo(
    () =>
      !isOrgList
        ? []
        : assignableMembers
            .filter(
              (m) =>
                reviewerPrincipalIds.has(m.principalId) || m.role === 'owner' || m.role === 'admin',
            )
            .map((m) => ({ principalId: m.principalId, label: m.label })),
    [isOrgList, assignableMembers, reviewerPrincipalIds],
  );

  // Permission model (server-enforced; this just drives affordances):
  //  - Managers (owner/admin of an Org Holder, or the sole owner of a Personal
  //    Holder, role 'self') can create / delete / reorder / assign and edit anything.
  //  - Plain members can ONLY edit the items / sections assigned to them.
  //  - Viewers are read-only.
  const userRole = selectedOption?.role ?? 'viewer';
  const isManager = !!list && (userRole === 'owner' || userRole === 'admin' || userRole === 'self');
  const isOwner = isManager; // "manage" gate: create / delete / reorder / assign
  const hasEditAccess = isManager; // action bar, undo/redo, settings are manager-only
  const canEditItem = (item: TodoItemResponse): boolean =>
    isManager || (!!principalId && item.assignedToPrincipalId === principalId);

  const [listSettingsOpen, setListSettingsOpen] = useState(false);
  // How the list view groups tasks: by section (default) or by project.
  const [groupBy, setGroupBy] = useState<'section' | 'project'>('section');
  // Filter visible tasks by assignee: 'all' | 'mine' | 'unassigned' | a principalId.
  const [assigneeFilter, setAssigneeFilter] = useState<AssigneeFilterValue>('all');
  // Filter by review status: 'both' | 'review' | 'regular' (review-mode lists only).
  const [reviewFilter, setReviewFilter] = useState<ReviewFilterValue>('both');
  // Free-text search across all of a task's loaded content (title, description,
  // display id, status, assignee, section). Narrows the sectioned view to
  // matching tasks and hides sections with no matches.
  const [searchQuery, setSearchQuery] = useState('');
  const searchTerms = useMemo(() => parseSearchTerms(searchQuery), [searchQuery]);
  const searchActive = searchTerms.length > 0;
  // Sub-task search text (parentItemId → text). Fetched only once the user
  // starts searching (they're otherwise loaded lazily per row).
  const subtaskSearchText = useTodoSubtaskIndex(selectedHolderId, listId!, searchActive);
  const reviewMode = list?.reviewMode ?? false;
  // "My Tasks" is not a filter like the others — selecting it in the Show: bar
  // (assignee value 'mine') swaps the shared list for the per-user board overlay
  // (org lists only). Every other Show: value ('all' = Everyone, 'unassigned', a
  // member) filters the shared list in place.
  const isMyTasksBoard = list?.holderType === 'org' && assigneeFilter === 'mine';

  const {
    canUndo,
    canRedo,
    undo,
    redo,
    captureSnapshot,
    clear: clearUndoHistory,
  } = useTodoUndoRedo({ holderId: selectedHolderId, listId: listId!, enabled: hasEditAccess });

  useTodoShortcuts({ onUndo: undo, onRedo: redo });

  // Clear undo history if the list itself changes substantively (e.g.,
  // the user navigates to a different list with the same component
  // still mounted, or membership changes flip what they can see).
  const prevListKeyRef = useRef<string>('');
  useEffect(() => {
    const key = JSON.stringify({ id: list?.listId, holder: list?.holderId });
    if (prevListKeyRef.current && prevListKeyRef.current !== key) {
      clearUndoHistory();
    }
    prevListKeyRef.current = key;
  }, [list?.listId, list?.holderId, clearUndoHistory]);

  // "Seen on open" notifications. Opening a list clears its badge counts (the
  // nav / worktop badge and the per-list badge) immediately, but keeps the
  // individual unread dots painted for the whole visit so the user can still see
  // exactly which items and sections were newly assigned. We FREEZE the unread
  // sets into `visitDots` the first time this list's notifications resolve, then
  // mark the list read. Because clearing is keyed by LIST (not by clicking each
  // rendered dot), a notification whose dot never renders — a completed item
  // dropped from the active view, or a section grant — still clears and can't get
  // stuck. Re-snapshots per list (the component stays mounted across list nav).
  const [visitDots, setVisitDots] = useState<{
    items: Set<string>;
    sections: Set<string>;
    reviews: Set<string>;
  }>(() => ({ items: new Set(), sections: new Set(), reviews: new Set() }));
  const seenListRef = useRef<string | null>(null);
  useEffect(() => {
    if (!listId || !notificationsLoaded || seenListRef.current === listId) return;
    seenListRef.current = listId;
    setVisitDots({
      items: new Set(unreadItemIds),
      sections: new Set(unreadSectionIds),
      reviews: new Set(unreadReviewItemIds),
    });
    markListNotificationsRead(listId);
  }, [
    listId,
    notificationsLoaded,
    unreadItemIds,
    unreadSectionIds,
    unreadReviewItemIds,
    markListNotificationsRead,
  ]);

  // Dialog state
  const [createSectionOpen, setCreateSectionOpen] = useState(false);
  const [createTimeframeOpen, setCreateTimeframeOpen] = useState(false);
  const [createItemOpen, setCreateItemOpen] = useState(false);
  const [editSectionOpen, setEditSectionOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<TodoSectionResponse | null>(null);
  const [editItemOpen, setEditItemOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<TodoItemResponse | null>(null);
  // Cross-list move: which task/section (if any) the "Move to list…" dialog targets.
  const [moveTarget, setMoveTarget] = useState<
    | { kind: 'item'; item: TodoItemResponse }
    | { kind: 'section'; section: TodoSectionResponse }
    | null
  >(null);

  // Extracted handlers
  const handlers = useListDetailHandlers(
    {
      holderId: selectedHolderId ?? '',
      listId: listId!,
      reviewMode,
      sections,
      items: rawItems,
      createSection,
      updateSection,
      deleteSection,
      createItem,
      updateItem,
      deleteItem,
      reorderItems,
      captureSnapshot,
      undo,
    },
    {
      setCreateSectionOpen,
      setCreateTimeframeOpen,
      setCreateItemOpen,
      setEditSectionOpen,
      setEditingSection,
      setEditItemOpen,
      setEditingItem,
    },
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  // Derived data. `completed` and `cancelled` are terminal "closed" states —
  // both drop out of the active list and live in the Completed view.
  const activeItems = useMemo(
    () => rawItems.filter((i) => i.status !== 'completed' && i.status !== 'cancelled'),
    [rawItems],
  );

  // Count of items awaiting verification — drives the "In review" filter chip.
  const reviewCount = useMemo(
    () => activeItems.filter((i) => i.status === 'in_review').length,
    [activeItems],
  );

  const sectionById = useMemo(() => new Map(sections.map((s) => [s.sectionId, s])), [sections]);

  // The Show (assignee) and Review chips are pure DISPLAY filters that KEEP the
  // section layout — they just narrow which tasks appear within each section.
  // Search additionally hides sections with no match (see matchingSectionIds).
  // Drag/edit/add still resolve against the full activeItems set (see
  // handleDragEnd → resolveItemDrop), so moving a visible task writes a correct
  // full order even while others are hidden.
  const visibleItems = useMemo(() => {
    let result = activeItems;
    if (reviewFilter === 'review') result = result.filter((i) => i.status === 'in_review');
    else if (reviewFilter === 'regular') result = result.filter((i) => i.status !== 'in_review');
    if (assigneeFilter !== 'all') {
      result = result.filter((item) => {
        if (assigneeFilter === 'mine')
          return !!principalId && item.assignedToPrincipalId === principalId;
        if (assigneeFilter === 'unassigned') return !item.assignedToPrincipalId;
        return item.assignedToPrincipalId === assigneeFilter;
      });
    }
    if (searchTerms.length > 0) {
      result = result.filter((item) =>
        taskMatchesTerms(
          {
            title: item.title,
            description: item.description,
            displayId: list?.itemKey ? `${list.itemKey}-${item.seq}` : String(item.seq),
            statusLabel: STATUS_STYLES[item.status]?.label,
            assigneeName: resolvePrincipalName?.(item.assignedToPrincipalId),
            sectionName: item.sectionId ? sectionById.get(item.sectionId)?.name : null,
            extra: [subtaskSearchText.get(item.itemId)],
          },
          searchTerms,
        ),
      );
    }
    return result;
  }, [
    activeItems,
    assigneeFilter,
    reviewFilter,
    principalId,
    searchTerms,
    list?.itemKey,
    resolvePrincipalName,
    sectionById,
    subtaskSearchText,
  ]);

  // When searching, a section renders only if it (or a descendant) holds a
  // match. Walk each matched item's section chain up to the root so ancestors of
  // a nested match stay visible. Empty when no search is active.
  const matchingSectionIds = useMemo(() => {
    const ids = new Set<string>();
    if (searchTerms.length === 0) return ids;
    for (const item of visibleItems) {
      let sid = item.sectionId;
      while (sid && !ids.has(sid)) {
        ids.add(sid);
        sid = sectionById.get(sid)?.parentSectionId ?? null;
      }
    }
    return ids;
  }, [searchTerms, visibleItems, sectionById]);

  const topLevelSections = useMemo(
    () =>
      sections.filter((s) => s.parentSectionId === null).sort((a, b) => a.sortOrder - b.sortOrder),
    [sections],
  );

  // While searching, drop top-level sections that hold no match (nested matches
  // keep their ancestors — see matchingSectionIds).
  const visibleTopLevelSections = useMemo(
    () =>
      searchActive
        ? topLevelSections.filter((s) => matchingSectionIds.has(s.sectionId))
        : topLevelSections,
    [searchActive, topLevelSections, matchingSectionIds],
  );

  const unsortedItems = useMemo(
    () => activeItems.filter((item) => item.sectionId === null),
    [activeItems],
  );
  // The Unsorted bucket honors the same filters (keeps its drop-zone role).
  const visibleUnsortedItems = useMemo(
    () => visibleItems.filter((item) => item.sectionId === null),
    [visibleItems],
  );

  const topLevelSectionIds = useMemo(
    () => visibleTopLevelSections.map((s) => s.sectionId),
    [visibleTopLevelSections],
  );

  // ---- Unified drag-and-drop: one context spans all sections + items, so a
  // task can be dragged between sections (and into/out of Unsorted). Section
  // drags reorder siblings; item drags reorder in place or move across sections.
  const [activeDrag, setActiveDrag] = useState<{ type: 'item' | 'section'; label: string } | null>(
    null,
  );
  const allSectionIds = useMemo(() => new Set(sections.map((s) => s.sectionId)), [sections]);
  const itemById = useMemo(() => new Map(activeItems.map((i) => [i.itemId, i])), [activeItems]);

  const siblingsOf = (parentSectionId: string | null): TodoSectionResponse[] =>
    sections
      .filter((s) => (s.parentSectionId ?? null) === parentSectionId)
      .sort((a, b) => a.sortOrder - b.sortOrder);

  // A section drag only targets its own siblings; an item drag excludes the
  // section-card droppables so it only lands on item lists / sibling items.
  // Shared with the My Tasks board (see makeTodoCollisionDetection in lib/dnd).
  const collisionDetection = makeTodoCollisionDetection({
    siblingSectionIdsOf: (id) =>
      siblingsOf(sectionById.get(id)?.parentSectionId ?? null).map((s) => s.sectionId),
    allSectionIds,
  });

  function handleDragStart(e: DragStartEvent) {
    const id = String(e.active.id);
    if (e.active.data.current?.type === 'section') {
      setActiveDrag({ type: 'section', label: sectionById.get(id)?.name ?? '' });
    } else {
      // Dragging a task that's part of a multi-selection drags the whole group.
      const multi = selectionCount > 1 && selectedIds.has(id);
      setActiveDrag({
        type: 'item',
        label: multi ? `${selectionCount} tasks` : (itemById.get(id)?.title ?? ''),
      });
    }
  }

  function handleDragEnd(e: DragEndEvent) {
    setActiveDrag(null);
    const { active, over } = e;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    if (active.data.current?.type === 'section') {
      if (activeId === overId) return;
      const parentId = sectionById.get(activeId)?.parentSectionId ?? null;
      const sibs = siblingsOf(parentId);
      const oldIndex = sibs.findIndex((s) => s.sectionId === activeId);
      const newIndex = sibs.findIndex((s) => s.sectionId === overId);
      if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;
      handlers.optimisticSectionReorder(
        arrayMove(sibs, oldIndex, newIndex).map((s) => s.sectionId),
      );
      return;
    }

    const result = resolveItemDrop({ activeId, overId, items: activeItems, isManager });
    if (!result) return;
    const multiDrag = selectionCount > 1 && selectedIds.has(activeId);
    // Multi-drag: dropping a selected task into a DIFFERENT section moves the whole
    // selection there.
    if (result.kind === 'move' && multiDrag) {
      void runBulk('Moved', (id) =>
        updateItem({ itemId: id, input: { sectionId: result.destSection } }),
      );
      return;
    }
    // Multi-reorder: dropping a selected task WITHIN its section slides the whole
    // selected block (kept in order) to the drop position.
    if (result.kind === 'reorder' && multiDrag) {
      const section = itemById.get(activeId)?.sectionId ?? null;
      // A within-section reorder can only arrange tasks in THIS section. If the
      // selection spans other sections, sliding the block here would silently
      // strand the rest — reject and explain, keeping the selection so the user
      // can move them into one section first (a cross-section drop still moves the
      // whole selection via the `move` branch above).
      const spansSections = [...selectedIds].some(
        (id) => (itemById.get(id)?.sectionId ?? null) !== section,
      );
      if (spansSections) {
        toast({
          title: 'Can’t reorder across sections',
          description:
            'Your selection spans multiple sections. Move them into one section first, then reorder.',
          variant: 'destructive',
        });
        return;
      }
      const destIds = activeItems
        .filter((i) => (i.sectionId ?? null) === section)
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((i) => i.itemId);
      const overContainer = parseItemContainerId(overId).isContainer;
      handlers.optimisticItemReorder(
        reorderSelectedIds(destIds, selectedIds, activeId, overContainer ? null : overId),
      );
      clearSelection();
      return;
    }
    if (result.kind === 'reorder') {
      handlers.optimisticItemReorder(result.order);
    } else {
      handlers.optimisticItemMove(result.itemId, result.destSection, result.order);
    }
  }

  // Section ids the current principal can manage tasks in: every section they
  // hold a direct access grant on, PLUS all of its descendants (the subsection
  // cascade). Managers see everything, so this only matters for plain members.
  const accessibleSectionIds = useMemo(() => {
    const result = new Set<string>();
    if (!principalId) return result;
    const childrenOf = new Map<string, string[]>();
    for (const s of sections) {
      if (s.parentSectionId) {
        const arr = childrenOf.get(s.parentSectionId) ?? [];
        arr.push(s.sectionId);
        childrenOf.set(s.parentSectionId, arr);
      }
    }
    const markSubtree = (id: string) => {
      if (result.has(id)) return;
      result.add(id);
      for (const child of childrenOf.get(id) ?? []) markSubtree(child);
    };
    for (const [sectionId, principals] of accessBySection) {
      if (principals.includes(principalId)) markSubtree(sectionId);
    }
    return result;
  }, [sections, accessBySection, principalId]);

  // The item editor's Section picker mirrors the server's move gate: a
  // non-manager may only re-file a task into a section they have access to, and
  // may not move it to Unsorted. Offer only those sections (plus the task's
  // current one, so it stays selectable) and hide "Unsorted" for members.
  // Managers get the full list, Unsorted included.
  const editorSectionOptions = isManager
    ? sections
    : sections.filter(
        (s) => accessibleSectionIds.has(s.sectionId) || s.sectionId === editingItem?.sectionId,
      );
  const editorShowNoneSection = isManager || editingItem?.sectionId == null;

  // Same access gate, pre-flattened for the row's "Move to section" quick action:
  // a manager sees every section; a member only the sections they may move into.
  const movableSectionOptions = useMemo(
    () =>
      flattenSectionTree(
        isManager ? sections : sections.filter((s) => accessibleSectionIds.has(s.sectionId)),
      ).map(({ section, depth }) => ({ sectionId: section.sectionId, name: section.name, depth })),
    [isManager, sections, accessibleSectionIds],
  );

  // ---- Multi-select + bulk actions ----------------------------------------
  const { toast } = useToast();
  // A task is selectable if the caller can edit it (manager, assignee, or a
  // member with access to its section) — so bulk actions never hit a wall.
  const canSelectItem = useCallback(
    (i: TodoItemResponse) =>
      isManager ||
      (!!principalId && i.assignedToPrincipalId === principalId) ||
      (!!i.sectionId && accessibleSectionIds.has(i.sectionId)),
    [isManager, principalId, accessibleSectionIds],
  );
  // Flat visual order of selectable top-level tasks (a section's items first, then
  // its child sections depth-first, then Unsorted) — drives shift-click ranges.
  const orderedSelectableIds = useMemo(() => {
    const ids: string[] = [];
    const itemsOf = (sectionId: string | null) =>
      visibleItems
        .filter((i) => i.sectionId === sectionId && i.parentItemId == null)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    const childrenOf = (parentId: string | null) =>
      sections
        .filter((s) => (s.parentSectionId ?? null) === parentId)
        .sort((a, b) => a.sortOrder - b.sortOrder);
    const walk = (sectionId: string) => {
      for (const it of itemsOf(sectionId)) if (canSelectItem(it)) ids.push(it.itemId);
      for (const child of childrenOf(sectionId)) {
        if (!searchActive || matchingSectionIds.has(child.sectionId)) walk(child.sectionId);
      }
    };
    for (const s of visibleTopLevelSections) walk(s.sectionId);
    for (const it of visibleUnsortedItems) if (canSelectItem(it)) ids.push(it.itemId);
    return ids;
  }, [
    visibleTopLevelSections,
    visibleUnsortedItems,
    visibleItems,
    sections,
    canSelectItem,
    searchActive,
    matchingSectionIds,
  ]);
  const {
    selectedIds,
    count: selectionCount,
    clear: clearSelection,
    retain: retainSelection,
    handleSelectClick,
  } = useMultiSelect(orderedSelectableIds);
  // Prune the selection to what's still visible/selectable as the list changes.
  useEffect(() => {
    retainSelection(new Set(orderedSelectableIds));
  }, [orderedSelectableIds, retainSelection]);
  // Esc / click-off dismissal is owned by useMultiSelect (shared with the board).

  const selectedItems = useMemo(
    () => rawItems.filter((i) => selectedIds.has(i.itemId)),
    [rawItems, selectedIds],
  );
  // Send-for-review enabled only when EVERY selected task is review-ready (no
  // incomplete checklist items or sub-tasks) — the bulk mirror of the row gate.
  const selectionReviewReady =
    selectedItems.length > 0 &&
    selectedItems.every(
      (i) =>
        i.done &&
        (i.subtaskTotal ?? 0) <= (i.subtaskDone ?? 0) &&
        (i.checklistTotal ?? 0) <= (i.checklistDone ?? 0),
    );
  const bulkStatusOptions = isManager
    ? reviewMode
      ? [...WORK_STATUSES, COMPLETE_STATUS, CANCEL_STATUS]
      : [...WORK_STATUSES, CANCEL_STATUS]
    : WORK_STATUSES;
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkMoveOpen, setBulkMoveOpen] = useState(false);
  // Apply a mutation to every selected task, then clear + toast a summary that
  // surfaces partial failures (e.g. one task the caller couldn't touch).
  const runBulk = useCallback(
    async (verb: string, fn: (itemId: string) => Promise<unknown>) => {
      const ids = [...selectedIds];
      if (ids.length === 0) return;
      const results = await Promise.allSettled(ids.map((id) => fn(id)));
      const failed = results.filter((r) => r.status === 'rejected').length;
      const ok = ids.length - failed;
      clearSelection();
      if (failed === 0) {
        toast({ title: `${verb} ${ok} task${ok === 1 ? '' : 's'}` });
      } else {
        toast({
          title: `${verb} ${ok} of ${ids.length}`,
          description: `${failed} couldn't be updated.`,
          variant: 'destructive',
        });
      }
    },
    [selectedIds, clearSelection, toast],
  );
  // Bulk verbs applied over the selection — surfaced in a selected row's ⋯ menu
  // when >1 is selected (see TodoItemRow → BulkActionsMenuItems).
  const bulkActions = useMemo<BulkActionsProps>(
    () => ({
      members: assignableMembers,
      onAssign: (p) =>
        runBulk(p ? 'Assigned' : 'Unassigned', (id) =>
          p ? assignItem({ itemId: id, assigneePrincipalId: p }) : unassignItem(id),
        ),
      statusOptions: bulkStatusOptions,
      onSetStatus: (s) =>
        runBulk('Updated', (id) =>
          updateItem({ itemId: id, input: { status: s as TodoItemResponse['status'] } }),
        ),
      projects,
      onSetProject: (pid) =>
        runBulk('Updated', (id) => updateItem({ itemId: id, input: { projectId: pid } })),
      sectionOptions: movableSectionOptions,
      canMoveToUnsorted: isManager,
      onSetSection: (sid) =>
        runBulk('Moved', (id) => updateItem({ itemId: id, input: { sectionId: sid } })),
      reviewerTargets: reviewMode ? reviewerTargets : undefined,
      reviewReady: selectionReviewReady,
      reviewBlockReason:
        'All selected tasks must have their checklist and sub-tasks completed first.',
      onSendForReview: (rid) =>
        runBulk('Sent for review', (id) =>
          updateItem({
            itemId: id,
            input: { status: 'in_review', reviewAssignedToPrincipalId: rid },
          }),
        ),
      onMoveToList: hasMoveTargets ? () => setBulkMoveOpen(true) : undefined,
      onDelete: () => setBulkDeleteOpen(true),
    }),
    [
      assignableMembers,
      runBulk,
      assignItem,
      unassignItem,
      bulkStatusOptions,
      updateItem,
      projects,
      movableSectionOptions,
      isManager,
      reviewMode,
      reviewerTargets,
      selectionReviewReady,
      hasMoveTargets,
    ],
  );

  // Context value
  const contextValue: TodoListContextValue = useMemo(
    () => ({
      isOwner,
      currentUserId: user?.userId,
      currentPrincipalId: principalId ?? undefined,
      isListPublic: list?.isPublic ?? false,
      // While searching, gate child sections to those with a match; otherwise
      // show every section (SectionCard treats undefined as "no gating").
      visibleSectionIds: searchActive ? matchingSectionIds : undefined,
      itemKeyPrefix: list?.itemKey,
      reviewMode,
      onToggleItem: handlers.handleToggleItem,
      onApproveItem: handlers.handleApproveItem,
      onRejectItem: handlers.handleRejectItem,
      onEditItem: handlers.handleEditItem,
      onDeleteItem: handlers.handleDeleteItem,
      onSetItemStatus: handlers.handleSetItemStatus,
      onEditSection: handlers.handleEditSection,
      onDeleteSection: handlers.handleDeleteSection,
      // Only offer "Move to list…" when there's another list to move to; the
      // kebab items additionally gate on isOwner (manager-only).
      onMoveItem: hasMoveTargets ? (item) => setMoveTarget({ kind: 'item', item }) : undefined,
      onMoveSection: hasMoveTargets
        ? (section) => setMoveTarget({ kind: 'section', section })
        : undefined,
      onCreateItem: handlers.handleCreateItem,
      onCreateSection: createSection,
      onReorderItems: (_sectionId, itemIds) => handlers.optimisticItemReorder(itemIds),
      onReorderSections: handlers.optimisticSectionReorder,
      onUpdateItemTitle: handlers.handleUpdateItemTitle,
      onCreateItemBelow: handlers.handleCreateItemBelow,
      subtasksByParent,
      onCreateSubtask: async (parentItemId: string, title: string) => {
        await createItem({ title, parentItemId });
      },
      onToggleHiddenFromPublic: handlers.handleToggleHiddenFromPublic,
      onToggleItemHidden: handlers.handleToggleItemHidden,
      onAssignItem: (itemId, assigneePrincipalId) => {
        void assignItem({ itemId, assigneePrincipalId });
      },
      onUnassignItem: (itemId) => {
        void unassignItem(itemId);
      },
      onSetSectionAccess: (sectionId, principalIds) => {
        void setSectionAccess(sectionId, principalIds);
      },
      sectionAccessBySectionId: accessBySection,
      accessibleSectionIds,
      projectById,
      projects,
      onSetItemProject: (itemId, projectId) => {
        captureSnapshot();
        void updateItem({ itemId, input: { projectId } });
      },
      movableSectionOptions,
      canMoveToUnsorted: isManager,
      onSetItemSection: (itemId, sectionId) => {
        captureSnapshot();
        void updateItem({ itemId, input: { sectionId } });
      },
      showProjectOnTasks: list?.showProjectOnTasks ?? true,
      showSectionPathOnTasks: list?.showSectionPathOnTasks ?? true,
      resolveSectionPath: (sectionId) =>
        buildSectionPath(sectionId, (id) => {
          const s = sectionById.get(id);
          return s ? { name: s.name, parentId: s.parentSectionId } : undefined;
        }),
      assignableMembers,
      resolvePrincipalName,
      // Dots come from the frozen "seen on open" snapshot, so they stay visible
      // for the whole visit even though the counts were cleared on open.
      unreadAssignedItemIds: visitDots.items,
      unreadAssignedSectionIds: visitDots.sections,
      onViewAssignment: markAssignmentRead,
      unreadReviewItemIds: visitDots.reviews,
      onViewReview: markReviewRead,
      onRequestReview: (itemId: string, reviewerPrincipalId: string | null) => {
        void updateItem({
          itemId,
          input: { status: 'in_review', reviewAssignedToPrincipalId: reviewerPrincipalId },
        });
      },
      reviewerTargets,
      isListReviewer,
      selectedItemIds: selectedIds,
      selectionActive: selectionCount > 0,
      selectionCount,
      onItemSelect: handleSelectClick,
      bulkActions,
    }),
    [
      isOwner,
      reviewMode,
      list?.itemKey,
      list?.isPublic,
      user?.userId,
      principalId,
      handlers,
      subtasksByParent,
      createItem,
      createSection,
      assignItem,
      unassignItem,
      setSectionAccess,
      accessBySection,
      accessibleSectionIds,
      assignableMembers,
      resolvePrincipalName,
      visitDots,
      markAssignmentRead,
      markReviewRead,
      updateItem,
      captureSnapshot,
      reviewerTargets,
      isListReviewer,
      searchActive,
      matchingSectionIds,
      projectById,
      projects,
      movableSectionOptions,
      isManager,
      list?.showProjectOnTasks,
      list?.showSectionPathOnTasks,
      sectionById,
      hasMoveTargets,
      selectedIds,
      selectionCount,
      handleSelectClick,
      bulkActions,
    ],
  );

  if (switcherLoading || loading || sectionsLoading || itemsLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-96" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!selectedHolderId) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <p className="text-sm text-muted-foreground">Select a workspace to view this list.</p>
      </div>
    );
  }

  if (error || !list) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              Error
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">{error || 'List not found'}</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => navigate('/a/arche_todo_lists')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to lists
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <TodoListProvider value={contextValue}>
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/a/arche_todo_lists')}
              aria-label="Back to lists"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-semibold flex-1 min-w-0 truncate">{list.name}</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {hasEditAccess && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={undo}
                  disabled={!canUndo}
                  aria-label="Undo"
                  title="Undo (Ctrl+Z)"
                >
                  <Undo2 className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={redo}
                  disabled={!canRedo}
                  aria-label="Redo"
                  title="Redo (Ctrl+Shift+Z)"
                >
                  <Redo2 className="h-4 w-4" />
                </Button>
              </div>
            )}
            <Link to={`/a/arche_todo_lists/${listId}/completed`}>
              <Button variant="outline" size="sm">
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Completed
              </Button>
            </Link>
            {hasEditAccess && (
              <Button variant="outline" size="sm" onClick={() => setListSettingsOpen(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Settings
              </Button>
            )}
          </div>
          {list.description && <p className="text-sm text-muted-foreground">{list.description}</p>}
          {list.isPublic && list.publicSlug && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <Globe className="h-3.5 w-3.5 flex-shrink-0" />
              <span>Public link:</span>
              <Link
                to={`/a/arche_todo_lists/public/${list.publicSlug}`}
                className="text-primary hover:underline break-all"
              >
                /a/arche_todo_lists/public/{list.publicSlug}
              </Link>
            </div>
          )}
          {list.holderType === 'org'
            ? assignableMembers.length <= 1 && (
                <p className="text-xs text-muted-foreground">
                  Shared workspace — invite people from{' '}
                  <Link to="/workspaces" className="text-primary hover:underline">
                    Workspace Controls
                  </Link>{' '}
                  to assign work to them.
                </p>
              )
            : isOwner && (
                <p className="text-xs text-muted-foreground">
                  Personal list. To assign work to teammates, add it to a{' '}
                  <Link to="/workspaces" className="text-primary hover:underline">
                    shared workspace
                  </Link>
                  .
                </p>
              )}
        </div>

        {/* Action bar */}
        {!isMyTasksBoard && hasEditAccess && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setCreateTimeframeOpen(true)}>
              <Clock className="h-4 w-4 mr-2" />
              Add Timeframe
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCreateSectionOpen(true)}>
              <FolderPlus className="h-4 w-4 mr-2" />
              Add Section
            </Button>
            <Button variant="outline" size="sm" onClick={() => setCreateItemOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Add Item
            </Button>
          </div>
        )}

        {/* Search sits directly under the Add buttons; the Show / Review filters
            go on their own row below it. The Group-by toggle switches the same
            tasks between the section layout and a project-grouped view. */}
        {!isMyTasksBoard && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <TaskSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              resultCount={searchActive ? visibleItems.length : undefined}
            />
            <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
              {(['section', 'project'] as const).map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGroupBy(g)}
                  aria-pressed={groupBy === g}
                  className={cn(
                    'rounded-md px-3 py-1 text-sm font-medium transition-colors',
                    groupBy === g
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {g === 'section' ? 'Sections' : 'Projects'}
                </button>
              ))}
            </div>
          </div>
        )}

        {(list?.holderType === 'org' || reviewMode) && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            {list?.holderType === 'org' && (
              <AssigneeFilterBar
                value={assigneeFilter}
                onChange={setAssigneeFilter}
                members={assignableMembers}
                myPrincipalId={principalId}
                showMine={true}
              />
            )}
            {reviewMode && !isMyTasksBoard && (
              <ReviewFilterBar
                value={reviewFilter}
                onChange={setReviewFilter}
                reviewCount={reviewCount}
              />
            )}
          </div>
        )}

        {/* Slim "N selected" indicator — feedback + Clear. Actions live in a
            selected row's ⋯ menu (acts on the whole selection). Sticks as you
            scroll. */}
        {!isMyTasksBoard && <SelectionIndicator count={selectionCount} onClear={clearSelection} />}

        {/* Project-grouped view — the same (filtered) tasks under project headers;
            drag a task between groups to reassign its project. */}
        {!isMyTasksBoard && groupBy === 'project' && (
          <ProjectGroupedView
            items={visibleItems}
            projects={projects}
            isManager={isManager}
            canEditItem={(item) =>
              isManager ||
              (!!principalId && item.assignedToPrincipalId === principalId) ||
              (!!item.sectionId && accessibleSectionIds.has(item.sectionId))
            }
            onReassignProject={(itemId, projectId) => {
              captureSnapshot();
              void updateItem({ itemId, input: { projectId } });
            }}
          />
        )}

        {/* Tasks — the sectioned view; the Show / Review chips filter the items
            shown within each section (never a flat list). */}
        {!isMyTasksBoard && groupBy === 'section' && (
          <DndContext
            sensors={sensors}
            collisionDetection={collisionDetection}
            // Re-measure droppables continuously so the Unsorted zone (which
            // only appears once a drag starts) is a reliable drop target.
            measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveDrag(null)}
          >
            <div className="space-y-4">
              <SortableContext items={topLevelSectionIds} strategy={verticalListSortingStrategy}>
                {visibleTopLevelSections.map((section) => (
                  <SectionCard
                    key={section.sectionId}
                    section={section}
                    sections={sections}
                    items={visibleItems}
                  />
                ))}
              </SortableContext>

              {/* Unsorted bucket — an item drop-zone in the shared context, so
                    tasks can be dragged out of a section. Shown when it has items
                    or while an item is being dragged (a target to un-sort into). */}
              <UnsortedDropZone
                items={visibleUnsortedItems}
                visible={visibleUnsortedItems.length > 0 || activeDrag?.type === 'item'}
                canEditItem={canEditItem}
                isManager={isManager}
              />

              {visibleTopLevelSections.length === 0 && visibleUnsortedItems.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <p>
                    {searchActive
                      ? 'No tasks match your search.'
                      : unsortedItems.length === 0
                        ? 'This list is empty. Add sections or items to get started.'
                        : 'No tasks match this filter.'}
                  </p>
                </div>
              )}
            </div>
            <DragOverlay>
              {activeDrag ? (
                <div className="rounded-md border bg-background px-3 py-2 text-sm shadow-lg">
                  {activeDrag.label}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        )}

        {isMyTasksBoard && (
          <MyTasksBoard
            holderId={selectedHolderId}
            listId={listId!}
            reviewMode={reviewMode}
            reviewerTargets={reviewerTargets}
            canManageProjects={isManager}
            isListReviewer={isListReviewer}
            showProjectOnTasks={list?.showProjectOnTasks ?? true}
            showSectionPathOnTasks={list?.showSectionPathOnTasks ?? true}
            itemKeyPrefix={list?.itemKey}
            assignableMembers={assignableMembers}
          />
        )}

        {/* Dialogs */}
        <CreateTimeframeDialog
          open={createTimeframeOpen}
          onClose={() => setCreateTimeframeOpen(false)}
          onCreate={handlers.handleCreateTimeframe}
        />
        <CreateSectionDialog
          open={createSectionOpen}
          onClose={() => setCreateSectionOpen(false)}
          onCreate={handlers.handleCreateSection}
          sections={sections}
        />
        <EditSectionDialog
          open={editSectionOpen}
          onClose={() => {
            setEditSectionOpen(false);
            setEditingSection(null);
          }}
          section={editingSection}
          sections={sections}
          onSave={handlers.handleSaveSection}
        />
        <EditItemDialog
          open={editItemOpen}
          onClose={() => {
            setEditItemOpen(false);
            setEditingItem(null);
          }}
          item={editingItem}
          sections={editorSectionOptions}
          showNoneSection={editorShowNoneSection}
          holderId={selectedHolderId}
          projects={projects}
          onCreateProject={isManager ? (name) => createProject({ name }) : undefined}
          onSave={handlers.handleSaveItem}
          // Who may edit the task's structure (checklist + sub-tasks): a manager,
          // the assignee, OR a member with access to the task's section (they
          // manage every task in that section, so its checklist is theirs to edit
          // too — mirrors the server's memberCanEditItem boundary). A reviewer
          // gets structure access via a separate in-review path in the dialog.
          canEditStructure={
            !editingItem ||
            isManager ||
            editingItem.assignedToPrincipalId === principalId ||
            (!!editingItem.sectionId && accessibleSectionIds.has(editingItem.sectionId))
          }
        />
        <CreateItemDialog
          open={createItemOpen}
          onClose={() => setCreateItemOpen(false)}
          onCreate={handlers.handleCreateItem}
          sections={sections}
        />
        <MoveToListDialog
          open={!!moveTarget}
          onClose={() => setMoveTarget(null)}
          mode={moveTarget?.kind === 'section' ? 'section' : 'item'}
          holderId={selectedHolderId}
          otherLists={otherLists}
          links={links}
          label={
            moveTarget?.kind === 'item'
              ? moveTarget.item.title
              : moveTarget?.kind === 'section'
                ? moveTarget.section.name
                : ''
          }
          onMove={async (targetListId, sectionId) => {
            if (!moveTarget) return;
            if (moveTarget.kind === 'item') {
              await moveItem({
                itemId: moveTarget.item.itemId,
                targetListId,
                targetSectionId: sectionId,
              });
            } else {
              await moveSection({
                sectionId: moveTarget.section.sectionId,
                targetListId,
                targetParentSectionId: sectionId,
              });
            }
          }}
        />
        {/* Bulk move-to-list — the toolbar's "Move to list…" opens this; picking a
            target list (+ section) moves every selected task there. */}
        <MoveToListDialog
          open={bulkMoveOpen}
          onClose={() => setBulkMoveOpen(false)}
          mode="item"
          holderId={selectedHolderId}
          otherLists={otherLists}
          links={links}
          label={`${selectionCount} task${selectionCount === 1 ? '' : 's'}`}
          onMove={async (targetListId, sectionId) => {
            setBulkMoveOpen(false);
            await runBulk('Moved', (id) =>
              moveItem({ itemId: id, targetListId, targetSectionId: sectionId }),
            );
          }}
        />
        {/* Bulk-delete confirm — the toolbar's Delete opens this. */}
        <Dialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>
                Delete {selectionCount} task{selectionCount === 1 ? '' : 's'}
              </DialogTitle>
              <DialogDescription>
                This removes the selected task{selectionCount === 1 ? '' : 's'} from the list. This
                can&rsquo;t be undone in bulk.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  setBulkDeleteOpen(false);
                  void runBulk('Deleted', (id) => deleteItem(id));
                }}
              >
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {hasEditAccess && (
          <ListSettingsDialog
            open={listSettingsOpen}
            onClose={() => setListSettingsOpen(false)}
            list={list}
            onSave={handlers.handleSettingsSave}
            onDelete={async () => {
              await handlers.handleDeleteList();
              navigate('/a/arche_todo_lists');
            }}
            assignableMembers={isOrgList ? assignableMembers : undefined}
            reviewerPrincipalIds={reviewerPrincipalIds}
            onSaveReviewers={isOrgList ? setReviewers : undefined}
            projects={projects}
            onCreateProject={(input) => createProject(input)}
            onUpdateProject={(projectId, input) => updateProject(projectId, input)}
            onDeleteProject={(projectId) => deleteProject(projectId)}
            links={links}
            otherLists={otherLists}
            onCreateLink={(targetListId) => createLink({ targetListId })}
            onDeleteLink={(linkId) => deleteLink(linkId)}
          />
        )}
      </div>
    </TodoListProvider>
  );
}

/**
 * The "Unsorted" bucket (items with no section). Its droppable node is always
 * mounted so it's a registered drop target the instant a drag begins; the card
 * chrome only shows when it has items or a drag is in progress (so a user can
 * drag a task out of a section into Unsorted even when it's currently empty).
 */
function UnsortedDropZone({
  items,
  visible,
  canEditItem,
  isManager,
}: {
  items: TodoItemResponse[];
  visible: boolean;
  canEditItem: (item: TodoItemResponse) => boolean;
  isManager: boolean;
}) {
  const { setNodeRef } = useDroppable({ id: itemContainerId(null) });
  return (
    <div ref={setNodeRef}>
      {visible && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Unsorted</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="min-h-[12px] space-y-1">
              <SortableContext
                items={items.map((i) => i.itemId)}
                strategy={verticalListSortingStrategy}
              >
                {items.map((item) => (
                  <TodoItemRow
                    key={item.itemId}
                    item={item}
                    canEdit={canEditItem(item)}
                    canManageSection={isManager}
                  />
                ))}
              </SortableContext>
              {items.length === 0 && (
                <p className="px-1 py-2 text-xs text-muted-foreground/70">
                  Drop a task here to unsort it
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
